import { useQuery } from "@apollo/client";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRight from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Maximize from "@mui/icons-material/Maximize";
import Minimize from "@mui/icons-material/Minimize";
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { MinimizableDialog } from "components/MinimizableDialog";
import {
  DocumentAiEnrichmentJobQuery,
  ExtractionStatus,
  JobStatus,
} from "generated-graphql/graphql";
import { terminalJobStatuses } from "hooks/useLongPollingForJob";
import { useTenant } from "hooks/useTenant";
import pluralize from "pluralize";
import { useDocumentReview } from "providers/documentReview";
import { useMinimizedDialogs } from "providers/minimizedDialogs";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { DOCUMENT_AI_ENRICHMENT_JOB_STATUS } from "./queries";

function ProgressChip({
  progressCount,
  totalCount,
  isFailed,
  isSucceeded,
}: {
  progressCount: number;
  totalCount: number;
  isFailed: boolean;
  isRunning: boolean;
  isSucceeded: boolean;
}) {
  const completedSuccessfully = isSucceeded && progressCount === totalCount;
  const completedUnsuccessfully = isSucceeded && progressCount < totalCount;
  if (isFailed || completedUnsuccessfully) {
    return (
      <Chip
        icon={<CloseIcon fontSize="small" color="error" />}
        label={`${progressCount} of ${totalCount}`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "error.main",
          backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
        }}
      />
    );
  }

  if (completedSuccessfully) {
    return (
      <Chip
        icon={<CheckIcon fontSize="small" color="success" />}
        label={`${progressCount} of ${totalCount}`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "success.main",
          backgroundColor: (theme) => alpha(theme.palette.success.main, 0.08),
        }}
      />
    );
  }
  return (
    <Chip
      icon={<CheckIcon fontSize="small" color="info" />}
      label={`${progressCount} of ${totalCount}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        color: "primary.main",
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
      }}
    />
  );
}

/**
 * Whether the review action is offered. A failed run offers it only to a caller
 * that opted in, whose review surface is where retry lives.
 */
function canOpenReview({
  isSucceeded,
  isFailed,
  reviewOnFailure,
}: {
  isSucceeded: boolean;
  isFailed: boolean;
  reviewOnFailure: boolean;
}) {
  return isSucceeded || (reviewOnFailure && isFailed);
}

/**
 * The documents handed to `onReview`. A failed run has no succeeded documents,
 * so filtering by status would hand an opted-in caller an empty list and
 * nothing to open; those callers take the whole job instead.
 */
function reviewableDocumentIds(
  documents: readonly { id: string; extractionStatus?: ExtractionStatus | null }[],
  reviewOnFailure: boolean
): string[] {
  const eligible = reviewOnFailure
    ? documents
    : documents.filter((doc) => doc.extractionStatus === ExtractionStatus.Succeeded);
  return eligible.map((doc) => doc.id);
}

export function DocumentAIAnalyzingDialog({
  open,
  onClose,
  jobId,
  preferOriginalTitle = false,
  dialogId,
  onReview,
  onComplete,
  wholeBoxClickable = false,
  onDiscard,
  discardLabel,
  onCloseWithoutReview,
  reviewOnFailure = false,
}: {
  open: boolean;
  onClose:
    | (() => void)
    | ((
        event: React.MouseEvent<HTMLElement>,
        reason: "backdropClick" | "escapeKeyDown" | "cancelButtonClick" | "closeButtonClick"
      ) => void);
  jobId: string;
  /** Unique ID for this dialog instance (for minimization) */
  dialogId?: string;
  preferOriginalTitle?: boolean;
  /** Custom review action. Receives IDs of successfully extracted documents. When omitted, navigates to the Documents page. */
  onReview?: (documentIds: string[]) => void;
  /** Called when the job reaches a terminal status (succeeded or failed). */
  onComplete?: () => void;
  /**
   * When true, clicking anywhere on the minimized chip (not just the icon)
   * expands it. The icon's circular hover affordance is also dropped so the
   * whole-chip click is the primary affordance.
   */
  wholeBoxClickable?: boolean;
  /**
   * Opt-in destructive "bail out" action, shown only once analysis is terminal.
   * The owner is responsible for the confirm + deletion; this just renders the
   * button. Callers that don't pass it get no discard affordance.
   */
  onDiscard?: () => void;
  /** Label for the discard button. Defaults to "Discard upload". */
  discardLabel?: string;
  /**
   * Called when the user dismisses the dialog (cancel/backdrop/escape) after a
   * successful run WITHOUT choosing to review — i.e. the uploads are saved but
   * unreviewed. Lets the owner surface a reassurance toast. Not called on the
   * review-and-confirm path, on discard, or when nothing extracted successfully.
   */
  onCloseWithoutReview?: (info: { succeededCount: number }) => void;
  /**
   * Opt-in: let the reviewer open the review surface after a FAILED run, and
   * hand `onReview` every document in the job rather than only the ones that
   * extracted. Off by default, so the review button stays disabled on failure
   * for every caller that does not ask for this.
   *
   * For an owner whose review surface is also where retry lives: without it, a
   * run that fails has no route to the retry control, which is the one case
   * retry exists for.
   */
  reviewOnFailure?: boolean;
}) {
  const { minimize, expand, close, dialogs } = useMinimizedDialogs();
  const { setDocumentsToReview } = useDocumentReview();
  const { data, error, stopPolling } = useQuery(DOCUMENT_AI_ENRICHMENT_JOB_STATUS, {
    variables: { id: jobId },
    pollInterval: 1000,
  });

  useEffect(() => {
    if (
      error ||
      (data?.documentAIEnrichmentJob?.status &&
        terminalJobStatuses.includes(data.documentAIEnrichmentJob.status))
    ) {
      stopPolling();
      onComplete?.();
    }
  }, [
    data?.documentAIEnrichmentJob?.status,
    data?.documentAIEnrichmentJob?.documents,
    error,
    stopPolling,
    setDocumentsToReview,
    onComplete,
  ]);

  const { tenantId } = useTenant();
  const navigate = useNavigate();

  // Generate dialog ID if not provided
  const currentDialogId = dialogId || `document-ai-analyzing-${jobId}`;

  // Get dialog mode
  const dialogMode = dialogs.find((d) => d.id === currentDialogId)?.mode ?? "centered";

  // Get job state from query
  const status: JobStatus | undefined = data?.documentAIEnrichmentJob?.status;
  const isRunning =
    status === JobStatus.Queued ||
    status === JobStatus.Dispatched ||
    status === JobStatus.Running ||
    status === undefined; // initial state
  const isSucceeded = status === JobStatus.Succeeded;
  const isFailed = status === JobStatus.Failed;

  const analysisStats = useMemo(() => {
    const documents = data?.documentAIEnrichmentJob?.documents ?? [];
    const succeededDocuments = documents.filter(
      (document) => document.extractionStatus === ExtractionStatus.Succeeded
    ).length;
    return {
      totalDocuments: documents.length,
      succeededDocuments,
    };
  }, [data?.documentAIEnrichmentJob?.documents]);

  const progressCount = analysisStats.succeededDocuments;
  const totalCount = analysisStats.totalDocuments;

  const handleMinimize = useCallback(() => {
    minimize(currentDialogId);
  }, [currentDialogId, minimize]);

  const handleExpand = useCallback(() => {
    expand(currentDialogId);
  }, [currentDialogId, expand]);

  // The primary "Review & Confirm" button closes via close(dialogId) directly,
  // so any onClose here (cancel button / backdrop / escape) is a
  // dismiss-without-review. Notify the owner only when there's reviewable data
  // (terminal + at least one success) so it can reassure the user the uploads
  // are saved-but-unreviewed.
  const handleClose = useCallback(
    (
      event: React.MouseEvent<HTMLElement>,
      reason: "backdropClick" | "escapeKeyDown" | "cancelButtonClick" | "closeButtonClick"
    ) => {
      if (!isRunning && isSucceeded && progressCount > 0) {
        onCloseWithoutReview?.({ succeededCount: progressCount });
      }
      onClose(event, reason);
    },
    [isRunning, isSucceeded, progressCount, onCloseWithoutReview, onClose]
  );

  // Always render MinimizableDialog component, with mode prop
  return (
    <MinimizableDialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      mode={dialogMode}
      wholeBoxClickable={wholeBoxClickable}
      onMinimizedClick={handleExpand}
      title={
        totalCount > 0 ? (
          <TitleBar
            progressCount={progressCount}
            totalCount={totalCount}
            isFailed={isFailed}
            isRunning={isRunning}
            isSucceeded={isSucceeded}
            numDocuments={totalCount}
          />
        ) : (
          // Skeleton for the title bar
          <Skeleton variant="text" width="50%" />
        )
      }
      hideCloseButton={true}
      additionalPrimaryActionElements={
        onDiscard && !isRunning
          ? [
              <Button
                key="discard"
                variant="text"
                color="error"
                size="medium"
                startIcon={<DeleteOutline />}
                onClick={onDiscard}
              >
                {discardLabel ?? "Discard upload"}
              </Button>,
            ]
          : undefined
      }
      titleBarActionElement={
        <IconButton
          onClick={(e) => {
            // When the whole minimized box is clickable, the paper-level handler
            // also fires; stop propagation so we don't expand twice.
            if (wholeBoxClickable && dialogMode === "minimizedInTray") {
              e.stopPropagation();
            }
            if (dialogMode === "minimizedInTray") {
              handleExpand();
            } else {
              handleMinimize();
            }
          }}
          // Drop the circular hover affordance when the whole chip is clickable;
          // the icon is then a hint, not the primary affordance.
          disableRipple={wholeBoxClickable && dialogMode === "minimizedInTray"}
          sx={
            wholeBoxClickable && dialogMode === "minimizedInTray"
              ? { "&:hover": { backgroundColor: "transparent" } }
              : undefined
          }
        >
          {dialogMode === "minimizedInTray" ? <Maximize /> : <Minimize />}
        </IconButton>
      }
      primaryActionButton={
        isRunning ? (
          <Button onClick={handleMinimize} variant="text" size="medium">
            Minimize
          </Button>
        ) : (
          <Button
            disabled={!canOpenReview({ isSucceeded, isFailed, reviewOnFailure })}
            onClick={() => {
              const documentIds = reviewableDocumentIds(
                data?.documentAIEnrichmentJob.documents ?? [],
                reviewOnFailure
              );

              if (onReview) {
                onReview(documentIds);
              } else if (documentIds.length > 0 && tenantId) {
                setDocumentsToReview(documentIds);
                navigate(`/o/${tenantId}/documents`);
              }
              close(currentDialogId);
            }}
            variant="text"
            size="medium"
            endIcon={isSucceeded ? <ChevronRight /> : undefined}
          >
            {isSucceeded && progressCount === 0 ? "Exit" : "Review & Confirm Details"}
          </Button>
        )
      }
      hideCancelButton={isRunning}
      cancelButtonProps={{ text: "Close" }}
    >
      <Stack spacing={2}>
        <Box>
          <LinearProgress
            value={isRunning && totalCount > 0 ? (progressCount / totalCount) * 100 : 100}
            variant={isRunning && progressCount === 0 ? "indeterminate" : "determinate"}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {isRunning &&
              "Hang tight, we're reading your uploads and filling in what we can. This usually takes about 5-10 seconds per file, though larger files may take longer. We'll let you know when everything is ready for you to review."}
            {isSucceeded &&
              `Your documents have been uploaded and we have filled in as much information as we could. When you have time, please review document classifications and details for each upload.`}
            {isFailed && "Analysis failed."}
          </Typography>
        </Box>

        <List dense>
          {totalCount > 0 ? (
            data?.documentAIEnrichmentJob?.documents.map((documentRecord) => {
              return (
                <DocumentAIAnalyzingRow
                  key={documentRecord.id}
                  documentRecord={documentRecord}
                  preferOriginalTitle={preferOriginalTitle}
                />
              );
            })
          ) : (
            <ListItem>
              <Skeleton variant="text" width="100%" />
            </ListItem>
          )}
        </List>
      </Stack>
    </MinimizableDialog>
  );
}

function TitleBar({
  progressCount,
  totalCount,
  isFailed,
  isRunning,
  isSucceeded,
  numDocuments,
}: {
  progressCount: number;
  totalCount: number;
  isFailed: boolean;
  isRunning: boolean;
  isSucceeded: boolean;
  numDocuments: number;
}) {
  const title = useMemo(() => {
    const pluralizedCount = pluralize("Document", numDocuments);
    if (isRunning) {
      return `📑 Analyzing ${numDocuments} ${pluralizedCount}...`;
    }
    if (isSucceeded) {
      return `🔔 Finished Analyzing  ${pluralizedCount}`;
    }
    if (isFailed) {
      return `❌ Analysis failed.`;
    }
    return `📑 Analysis complete.`;
  }, [isRunning, numDocuments, isSucceeded, isFailed]);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: (theme) => theme.spacing(2),
      }}
    >
      <Typography variant="h6">{title}</Typography>
      <ProgressChip
        progressCount={progressCount}
        totalCount={totalCount}
        isFailed={isFailed}
        isRunning={isRunning}
        isSucceeded={isSucceeded}
      />
    </Box>
  );
}

function DocumentAIAnalyzingRow({
  documentRecord,
  preferOriginalTitle = false,
}: {
  documentRecord: DocumentAiEnrichmentJobQuery["documentAIEnrichmentJob"]["documents"][number];
  preferOriginalTitle?: boolean;
}) {
  return (
    <ListItem
      key={documentRecord.id}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {preferOriginalTitle
            ? (documentRecord.originalTitle ?? documentRecord.title)
            : documentRecord.title}
        </Typography>
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 0.5,
        }}
      >
        <ExtractionStatusDisplay
          extractionStatus={documentRecord?.extractionStatus as ExtractionStatus | null | undefined}
          extractionError={documentRecord?.extractionError ?? null}
        />
      </Box>
    </ListItem>
  );
}

const ExtractionStatusDisplay = ({
  extractionStatus,
  extractionError,
}: {
  extractionStatus: ExtractionStatus | null | undefined;
  extractionError: string | null;
}) => {
  switch (extractionStatus) {
    case ExtractionStatus.Pending:
    case ExtractionStatus.NotAttempted:
    case undefined:
    default:
      return (
        <Typography variant="body2" color="primary.main">
          In progress...
        </Typography>
      );
    case ExtractionStatus.Succeeded:
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography variant="body2" color="success.main">
            Done
          </Typography>
          <CheckIcon fontSize="small" color="success" />
        </Box>
      );
    case ExtractionStatus.Failed:
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Tooltip title={extractionError ?? "Extraction failed"}>
            <ErrorOutlineIcon color="error" fontSize="small" />
          </Tooltip>
          <Typography variant="body2" color="error.main">
            Failed
          </Typography>
        </Box>
      );
  }
};
